import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Grid, IntlProvider as RSIntlProvider } from 'rsuite'
import AppContext from './context/AppContext'
// import enUS from '@rsuite-locales/en_US';
import {
  DirectionType,
  getDefaultTheme,
  getThemeCssPath,
  getThemeId,
  readTheme,
  ThemeType,
  writeTheme,
  writeFontSize,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
} from './utils/themeHelpers'
import loadCssFile from './utils/loadCssFile'
import Root from './Root'
import ThaiHealthChat from './components/chatBot/ThaiHealthChat'

export default function App({ pageProps }) {
  const [defaultThemeName, defaultDirection, defaultFontSize] = useMemo(readTheme, [
    getDefaultTheme(),
  ])
  const [themeName, setThemeName] = useState(defaultThemeName)
  const [direction, setDirection] = useState(defaultDirection)
  const [fontSize, setFontSize] = useState(defaultFontSize)
  const [language, setLanguage] = useState('zh')
  const [styleLoaded, setStyleLoaded] = useState(false)
  // const locale = language === 'zh' ? zhCN : enUS;

  const loadTheme = useCallback((themeName, direction) => {
    const themeId = getThemeId(themeName, direction)
    loadCssFile(getThemeCssPath(themeName, direction), themeId).then(() => {
      const html = document.querySelector('html')
      html.dir = direction
      writeTheme(themeName, direction)
      Array.from(document.querySelectorAll('[id^=theme]')).forEach((css) => {
        if (css.id !== themeId) {
          css.remove()
        }
      })
    })
  }, [])

  const onChangeTheme = useCallback(() => {
    const newThemeName = themeName === 'default' ? 'dark' : 'default'
    setThemeName(newThemeName)
    loadTheme(newThemeName, direction)
  }, [themeName, direction])

  useEffect(() => {
    // if (!canUseDOM) {
    //   return;
    // }
    const media = matchMedia('(prefers-color-scheme: dark)')
    media?.addEventListener?.('change', onChangeTheme)
    return () => {
      media?.removeEventListener?.('change', onChangeTheme)
    }
  }, [themeName, direction])

  const onChangeDirection = useCallback(() => {
    const newDirection = direction === 'ltr' ? 'rtl' : 'ltr'
    setDirection(newDirection)
    loadTheme(themeName, newDirection)
  }, [themeName, direction])

  const onChangeLanguage = useCallback((value) => {
    setLanguage(value)
  }, [])

  useEffect(() => {
    const [defaultThemeName, defaultDirection] = readTheme()
    const html = document.querySelector('html')
    html.dir = defaultDirection
    loadCssFile(
      getThemeCssPath(defaultThemeName, defaultDirection),
      getThemeId(defaultThemeName, defaultDirection)
    ).then(() => {
      setStyleLoaded(true)
    })
  }, [])

  useEffect(() => {
    document.documentElement.style.setProperty('font-size', `${fontSize}px`)
    writeFontSize(fontSize)
  }, [fontSize])

  const onChangeFontSize = useCallback((action) => {
    setFontSize((fs) => {
      const _fontSize = parseInt(fs, 10)
      if (action === 'PLUS') {
        if (_fontSize >= MAX_FONT_SIZE) {
          return fs
        }
        return _fontSize + 1
      } else {
        if (_fontSize <= MIN_FONT_SIZE) {
          return fs
        }
        return _fontSize - 1
      }
    })
  }, [])

  // const messages = getMessages(language)

  if (!styleLoaded) {
    return null
  }

  return (
    <Grid fluid className="app-container">
      <RSIntlProvider rtl={direction === 'rtl'}>
        <AppContext.Provider
          value={{
            language,
            localePath: language === 'zh' ? '/zh-CN' : '/en-US',
            theme: [themeName, direction],
            onChangeDirection,
            onChangeTheme,
            onChangeLanguage,
            onChangeFontSize,
            styleLoaded,
          }}
        >
          <Root {...pageProps} />

          <ThaiHealthChat />
        </AppContext.Provider>
      </RSIntlProvider>
    </Grid>
  )
}

App.getInitialProps = ({ ctx }) => {
  let pageProps = {
    userLanguage: 'en',
  }

  if (!process.browser) {
    pageProps = {
      userLanguage: ctx.query.userLanguage,
    }
  }

  return {
    pageProps,
  }
}
